import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import React, { memo } from 'react';
import { Option } from '@im/ui-lib/SelectOption';
import MenuItem from '@mui/material/MenuItem';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import Autocomplete, {
  AutocompleteChangeReason,
} from '@mui/material/Autocomplete';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import Paper from '@mui/material/Paper';
import { v4 as uuidV4 } from 'uuid';
import { SxProps, Theme, useTheme } from '@mui/system';

type Props = {
  'data-testid'?: string;
  keyOptionSelected?: Option;
  keyOptions: Option[];
  onClear?: () => void;
  onKeyOptionChange: (option: Option) => void;
  onSearch?: (value: string) => void;
  onValueChange: (value: string) => void;
  placeholder?: string;
  sx?: SxProps<Theme>;
  value: string;
  valueOptions?: string[];
};

const AutocompleteSearchBar = (props: Props) => {
  const theme = useTheme();
  const handleOnChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: string | null,
    reason: AutocompleteChangeReason
  ) => {
    if (reason === 'selectOption') props.onSearch?.(value ?? '');
    if (reason === 'clear') props.onClear?.();
  };
  const handleKeyDown = (event: any) => {
    if (event?.target.value)
      if (event.key === 'Enter') {
        event.preventDefault();
        event?.target?.blur();
        props.onSearch?.(event?.target?.value);
      }
  };
  return (
    <Stack
      alignItems={'self-end'}
      data-testid={props['data-testid']}
      flexDirection={'row'}
      sx={props.sx}
      width={450}
    >
      <TextField
        SelectProps={{
          MenuProps: { elevation: 1 },
        }}
        data-testid={`${props['data-testid']}-key-options`}
        inputProps={{
          IconComponent: () => <ExpandMoreOutlinedIcon fontSize="small" />,
        }}
        select
        sx={{
          '.MuiInputBase-input': {
            color: 'inherit !important',
            lineHeight: `23px !important`,
          },
          '.MuiOutlinedInput-notchedOutline': {
            borderBottomRightRadius: 0,
            borderRight: 'none',
            borderTopRightRadius: 0,
          },

          minWidth: 150,
        }}
        value={props.keyOptionSelected?.value}
      >
        {props.keyOptions.map((option) => (
          <MenuItem
            dense
            key={option.value}
            onClick={(e) => props.onKeyOptionChange(option)}
            selected={props.keyOptionSelected === option}
            sx={{
              px: 1,
            }}
            value={option.value}
          >
            {option.label}
          </MenuItem>
        ))}
      </TextField>
      <Autocomplete
        PaperComponent={({ children }) => (
          <Paper
            sx={{
              display: !(props.valueOptions && props.valueOptions?.length > 0)
                ? 'none'
                : 'block',
              padding: 1,
            }}
          >
            {children}
          </Paper>
        )}
        data-testid={`${props['data-testid']}-autocomplete`}
        forcePopupIcon={false}
        fullWidth
        getOptionLabel={(option) => option}
        onChange={handleOnChange}
        onInputChange={(e, v, r) => props.onValueChange?.(v)}
        onKeyDown={handleKeyDown}
        options={props.valueOptions ?? []}
        renderInput={(params) => (
          <TextField
            {...params}
            InputProps={{
              ...params.InputProps,
              autoComplete: 'off',
              placeholder: props.placeholder,
              startAdornment: (
                <InputAdornment position="start">
                  <IconButton onClick={() => props.onSearch?.(props.value)}>
                    <SearchIcon className="SearchIcon" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        )}
        renderOption={(renderProps, option, a) => (
          <MenuItem {...renderProps} dense key={uuidV4()} sx={{ px: 1 }}>
            {option}
          </MenuItem>
        )}
        sx={{
          '&:focus-within .SearchIcon': {
            fill: theme.palette.primary.main,
          },
          '.MuiInputBase-root': {
            borderBottomLeftRadius: 0,
            borderTopLeftRadius: 0,
          },
        }}
        value={props.value}
      />
    </Stack>
  );
};

export default memo(AutocompleteSearchBar);
